<script lang="ts">
import FeIcon from '@/components/global/icon.vue'
import { IonButton } from '@ionic/vue'
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'
import { ContextPageGetters } from '@/store/context-page/context-page'

export default defineComponent({
  name: 'FeButton',

  components: {
    IonButton,
    FeIcon,
  },

  props: {
    /* eslint-disable vue/require-default-prop */
    // @TODO set default value
    icon: {
      type: String,
      required: false,
      default: '',
    },

    label: {
      type: String,
      required: false,
      default: '',
    },

    disabled: {
      type: [Boolean, String],
      default: false,
    },

    color: {
      type: String,
      default: 'shadow',
      validator: (value: string) => {
        return [
          'base',
          'primary',
          'shadow',
          'border',
          'warn',
          'icon',
          'medium',
          'info',
        ].includes(value)
      },
    },

    fill: {
      type: String,
      default: 'clear',
      validator: (value: string) => {
        return ['default', 'clear', 'outline', 'solid'].includes(value)
      },
    },

    type: {
      type: String,
      default: 'button',
    },
  },

  emits: ['click'],

  computed: {
    ...mapGetters({
      getContextPage: ContextPageGetters.GET_CONTEXT_PAGE,
    }),
    castedFill() {
      return this.fill as 'default' | 'clear' | 'outline' | 'solid'
    },
    castedType() {
      return this.type as 'button' | 'submit' | 'reset'
    },
    isDisabled(): boolean {
      return this.disabled === true || this.disabled === 'true'
    },
    context(): string {
      if (this.color === 'primary') {
        const context = this.getContextPage as string | undefined
        return context?.toLowerCase() || ''
      }
      return ''
    },
  },

  methods: {
    onClick(e: Event) {
      this.$emit('click', e as Event)
    },
  },
})
</script>

<template>
  <ion-button
    :type="castedType"
    :class="color"
    :disabled="isDisabled"
    :color="context"
    :fill="castedFill"
    @click="onClick"
  >
    <div class="fe-button-content">
      <slot name="icon">
        <fe-icon v-if="icon" :icon="icon"></fe-icon>
      </slot>

      <slot>
        <span v-if="label && color !== 'icon'">{{ label }}</span>
      </slot>

      <slot name="icon-right"></slot>
    </div>
  </ion-button>
</template>

<style lang="scss" scoped>
@import '@/styles/_constants.scss';
@import '@/styles/_mixins.scss';

$context-colors: (
  'frontoffice': rgb(var(--color-primary)),
  'backoffice': rgb(var(--color-secondary)),
  'stock': rgb(var(--color-primary)),
  'custom_date': rgb(var(--color-alert)),
);

$context-colors-dark: (
  'frontoffice': rgb(var(--color-primary-dark)),
  'backoffice': rgb(var(--color-secondary-dark)),
  'stock': rgb(var(--color-tertiary-dark)),
  'custom_date': rgb(var(--color-alert)),
);

@mixin apply-color($context) {
  &.ion-color-#{$context} {
    --color: rgb(var(--color-white));
    --fill: rgb(var(--color-white));
    background-color: map-get($map: $context-colors, $key: $context);
    &:hover,
    &:active {
      background-color: map-get($map: $context-colors-dark, $key: $context);
    }
  }
}

ion-button {
  --background: rgb(var(--color-white));
  --border-radius: 0;
  --border-width: 1px;
  --border-style: solid;
  --box-shadow: none;
  --border-color: rgb(var(--color-black));
  --color: rgb(var(--color-black));
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 15px;
  --padding-end: 15px;

  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  text-transform: uppercase;
  line-height: 1;
  letter-spacing: 0.03em;
  border-radius: 0.8rem;

  @include tablet-small-landscape-up {
    --border-radius: 0.8rem;
  }

  .fe-button-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
  }

  &:disabled {
    opacity: 0.5;
  }

  &.primary {
    --border-style: none;
    --background: rgb(var(--color-primary));
    --color: rgb(var(--color-white));
    --border-radius: 0.8rem;
    border-radius: 0.8rem;
  }
  &.base {
    --border-style: none;
    --background: rgb(var(--color-grey-500));
    --color: rgb(var(--color-white));
    &:hover,
    &:active {
      --background: #{$color-dark-grey};
    }
  }

  &.shadow {
    --border-style: none;
    box-shadow: 0 0.1rem 0.7rem rgb(0 0 0 / 22%);
    &:hover,
    &:active,
    &:focus {
      --background: rgb(var(--color-grey-100));
    }
  }

  &.medium {
    --border-style: none;
    --background: #{$color-medium-grey};
    --color: rgb(var(--color-white));
    &:hover,
    &:active {
      --background: #{$color-dark-grey};
    }
  }
  &.info {
    --border-style: none;
    --background: rgb(var(--color-info));
    --color: rgb(var(--color-white));
    &:hover,
    &:active,
    &:focus {
      --background: rgb(var(--color-info));
    }
  }

  &.border {
    --background: transparent;
    &:hover,
    &:active,
    &:focus {
      --background: rgba(var(--color-black) / var(--opacity-xs));
    }
  }

  &.icon {
    --border-style: none;
    --background: transparent;

    --padding-start: 0;
    --padding-end: 0;
  }

  &.warn {
    --border-color: rgb(var(--color-error));
    --color: rgb(var(--color-error));
    --background: transparent;

    &:hover,
    &:active,
    &:focus {
      --background: rgba(var(--color-button-hover-warn) / var(--opacity-xs));
    }

    .fe-icon {
      fill: rgb(var(--color-error));
    }
  }

  :deep(.fe-icon) {
    width: 13px;
    height: 13px;
    max-height: 1.3rem;
    max-width: 1.3rem;

    ion-icon {
      height: 100%;
      width: 100%;
      min-width: 0;
      min-height: 0;
    }
  }

  @include apply-color('frontoffice');
  @include apply-color('backoffice');
  @include apply-color('stock');
  @include apply-color('custom_date');
}
</style>
